import { Controller } from '@hotwired/stimulus'

import { createConsumer } from '@rails/actioncable'

// The subscribe identifier names no dock; the `observe` message names it instead.
const CHANNEL = { channel: 'DockControlChannel' }

// A DRC session reports often, so the feed keeps only the most recent messages on screen.
const MAX_MESSAGES = 50

// Streams the DJI Status Dock Control channel, the only place a command's outcome is reported.
export default class extends Controller {
  static classes = [
    'error'
  ]

  static targets = [
    'status',
    'received',
    'messages',
    'empty'
  ]

  static values = {
    cableUrl: String,
    // The dock's serial number, sent as part of the `observe` message payload.
    serialNumber: String,
    // The observer access token, sent as part of the `observe` message payload.
    accessToken: String,
    received: {
      type: Number,
      default: 0
    }
  }

  connect () {
    this.setStatus('Connecting...', false)

    this.consumer = createConsumer(this.cableUrlValue)

    this.subscription = this.consumer.subscriptions.create(CHANNEL, {
      received: (data) => {
        this.receive(data)
      },

      // The server streams nothing until it receives a valid `observe` message.
      connected: () => {
        this.setStatus('Connected (observing...)', false)
        this.subscription.perform('observe', {
          serial_number: this.serialNumberValue,
          access_token: this.accessTokenValue
        })
      },

      disconnected: () => {
        this.setStatus('Disconnected', true)
      },

      rejected: () => {
        this.setStatus('Rejected', true)
      }
    })
  }

  disconnect () {
    this.subscription?.unsubscribe()
    this.consumer?.disconnect()
  }

  receive (data) {
    this.receivedValue++
    this.receivedTarget.textContent = this.receivedValue

    // A refused `observe` says nothing about the dock, so it belongs in the status not the log.
    if (data.type === 'observe_rejected') {
      this.setStatus(`Rejected (${data.serial_number})`, true)
      return
    }

    this.setStatus('Streaming', false)

    this.emptyTarget.classList.add('d-none')
    this.messagesTarget.prepend(this.buildRow(data))

    while (this.messagesTarget.children.length > MAX_MESSAGES) {
      this.messagesTarget.lastElementChild.remove()
    }
  }

  buildRow (data) {
    const row = document.createElement('tr')

    // The dock does not always send a timestamp, so an absent one means "just now".
    this.appendCell(row, data.timestamp ? new Date(data.timestamp).toLocaleTimeString() : 'just now')
    this.appendCell(row, data.type || 'unknown')
    this.appendCell(row, this.describe(data), this.isFailure(data))
    this.appendCell(row, data.transaction_id || '')

    return row
  }

  appendCell (row, text, isError = false) {
    const cell = document.createElement('td')
    cell.textContent = text

    this.errorClasses.forEach((errorClass) => {
      cell.classList.toggle(errorClass, isError)
    })

    row.appendChild(cell)
  }

  // Says what a message reports in DJI's own words, which are safe to show to a user.
  describe (data) {
    switch (data.type) {
      case 'command':
        return [`${data.command}: ${data.status}`, data.status_message].filter(Boolean).join(' - ')
      case 'drc_status':
        return data.drc_state
      case 'joystick_invalid':
        return data.reason_message || data.reason
      default:
        // A type this page does not recognise is still worth reporting.
        return JSON.stringify(data)
    }
  }

  // A refused command and lost flight control input are worth picking out of the log.
  isFailure (data) {
    return data.status === 'rejected' || data.type === 'joystick_invalid'
  }

  setStatus (text, isError) {
    this.statusTarget.textContent = text

    this.errorClasses.forEach((errorClass) => {
      this.statusTarget.classList.toggle(errorClass, isError)
    })
  }
}
